<template>
  <div
    v-show="!isAuthenticated"
    class="bg-surface-level-50 dark:bg-surface-level-950/50 dark:border-border-inverse/10 hidden lg:block"
  >
    <!-- Horizontal padding matches main nav: px-4 lg:px-8 (same as p-4 lg:px-8 on nav) -->
    <div
      class="flex items-center lg:px-8 px-4 py-1.5"
      :class="layout === 'split' ? 'justify-between' : 'justify-end'"
    >
      <div
        v-if="layout === 'split'"
        class="flex-1 hidden lg:block min-w-0"
        aria-hidden="true"
      />

      <div
        class="flex justify-end min-w-0"
        :class="layout === 'split' ? 'flex-1' : ''"
      >
        <nav
          class="dark:text-content-level-400 flex font-normal gap-x-6 items-center text-content-level-600 text-xs"
          :aria-label="$t('commerce.guestTopBar.utilityNavigationLabel')"
        >
          <NuxtLink
            to="/about-us"
            class="dark:hover:text-primary hover:text-content-level-900"
          >
            {{ $t('commerce.guestTopBar.about') }}
          </NuxtLink>

          <NuxtLink
            to="/contact"
            class="dark:hover:text-primary hover:text-content-level-900"
          >
            {{ $t('commerce.guestTopBar.contact') }}
          </NuxtLink>

          <UButton
            color="neutral"
            variant="link"
            size="xs"
            class="font-medium hover:text-content-level-500 opacity-75 text-[11px] text-content-level-400"
            v-bind="languageButtonAttrs"
            @click="toggleLocale"
          >
            {{ languageSwitchLabel }}
          </UButton>

          <ClientOnly>
            <UColorModeButton
              v-bind="colorModeButtonAttrs"
              color="neutral"
              size="xs"
              variant="ghost"
            />

            <template #fallback>
              <USkeleton class="size-6" />
            </template>
          </ClientOnly>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useLanguageSwitch } from '~/composables/useLanguageSwitch'
import { useIdentity } from '~/composables/useIdentity'

withDefaults(
  defineProps<{
    /** Top-bar layout variant. */
    layout?: 'split' | 'trailing'
  }>(),
  { layout: 'trailing' }
)

const { isAuthenticated } = useIdentity()
const t = usePresentationTranslator()
const {
  toggleLocale,
  switchAriaLabel: languageAriaLabel,
  switchLabel: languageSwitchLabel
} = useLanguageSwitch()
const colorModeButtonAttrs = computed(() => ({
  'aria-label': t('commerce.guestTopBar.toggleColorModeAriaLabel')
}))
const languageButtonAttrs = computed(() => ({ 'aria-label': languageAriaLabel.value }))
</script>
